/* eslint-disable jsx-a11y/anchor-is-valid */
import Box from "../Box";
import { Table } from "antd";
import useBreakPoint from "../../utils/useBreakPoint";
import { CPopover, CText } from "@carrefour/design-system-components-react";
import styled from "styled-components";
import { ColumnsType } from "antd/es/table";
import useSWR from "swr";
import { postAlertFetcher } from "../../utils/fetcher";
import { SynthesisAlertResponse } from "../../types/synthesis";
import { useTerritories } from "../../context/territoriesContext";
import { useTranslation } from "react-i18next";
import { useStores } from "../../context/storesContext";
import { useNavigate } from "react-router";
import { formatCurrency } from "../../utils/numberUtils";

const TextEllipsis = styled(CText)`
  width: 100%;
  height: 100%;
  font-family: system-ui;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
`;
const Popover = styled(CPopover)`
  .c-popover__box {
    width: calc(100% + 80px);
  }
  > * .c-popover__header p {
    font-family: system-ui;
    font-size: small;
  }
`;
const LinkedBox = styled.div`
  cursor: pointer;
  & > * {
    color: var(--Interactive-Content-Main-Active, #0970E6);
  }
`;

const indicators = ["toProcess", "inProgress", "toAdjust", "resolved", "total", "totalImpactFinancier"];

const RegionAlertTable = () => {
  const { t } = useTranslation();
  const { stores,selectStore } = useStores();
  const navigate = useNavigate();

  const indicatorLabels: Record<string, string> = {
    toProcess: t("alerts.synthesis.toProcess"),
    inProgress: t("alerts.synthesis.inProgress"),
    toAdjust: t("alerts.synthesis.toAdjust"),
    resolved: t("alerts.synthesis.resolved"),
    total: t("alerts.synthesis.total"),
    totalImpactFinancier: t("alerts.synthesis.totalImpactFinancier"),
  };

  const isBreakpoint = useBreakPoint();

  const { selectedTerritory } = useTerritories();
  const goToStore = (key: string) => {
    selectStore(key);
    navigate("/alert");
  }
  const storesKeys: string[] = stores.map(store => store.storeKey);
  const columns = [
    {
      title: () => (
        <Box>
          <TextEllipsis bold size={"s"}>
            {t("commercial.magasin")}
          </TextEllipsis>
        </Box>
      ),
      dataIndex: "storeName",
      key: "storeName",
      zIndex: 10,
      fixed: "left",
      width: isBreakpoint === "phone" ? 120 : 200,
      sorter: (a: any, b: any) => a.storeName.localeCompare(b.storeName),
      render: (text: string, record: any) =>
        isBreakpoint === "phone" ? (
          <Popover
            title={text}
            position={"top-right"}
            buttonContent={ storesKeys.includes(record?.key) ?
                          <LinkedBox onClick={()=>goToStore(record?.key)}>
                              <TextEllipsis size={"s"}>{text}</TextEllipsis>
                          </LinkedBox>:
                          <TextEllipsis size={"s"}>{text}</TextEllipsis>
                        }
          />
        ) : (
          storesKeys.includes(record?.key) ?
          <LinkedBox onClick={()=>goToStore(record?.key)}>
              <TextEllipsis size={"m"}>{text}</TextEllipsis>
          </LinkedBox>:
          <TextEllipsis size={"m"}>{text}</TextEllipsis>
        ),
    },
    ...indicators.map((indicator) => ({
      title: () => (
        <Box>
          <TextEllipsis bold size={"s"}>
            {indicatorLabels[indicator]}
          </TextEllipsis>
        </Box>
      ),
      dataIndex: indicator,
      key: indicator,
      width: 130,
      sorter: (a: any, b: any) => a[indicator] - b[indicator],
      render: (text: number) => {
        return {
          props: { style: { backgroundColor: "transparent" } },
          children: (
            <TextEllipsis bold={indicator === "total" || indicator === "totalImpactFinancier"}
              size={isBreakpoint === "phone" ? "s" : "m"}>
              {indicator === "totalImpactFinancier"
                ? (text === 0 ? "Non chiffré" : formatCurrency(text))
                : (text === 0 ? "-" : text)}
            </TextEllipsis>
          ),
        };
      },
    })),
  ] as ColumnsType;

  const storesBody = selectedTerritory?.stores?.map(store => ({
   storeKey: store.storeKey,
    storeName: store.storeName
  })) || [];

  const { data: synthesis, isLoading } = useSWR<SynthesisAlertResponse>(
  selectedTerritory?.key 
    ? [`/synthesis/alerts/month`, storesBody] 
    : null,
  ([url, body]) => postAlertFetcher(url, body)
);

  const storesMapped = synthesis?.alerts?.map((alertDto) => {
    return {
      key: alertDto?.storeKey,
      storeName: alertDto?.storeName?.split("(")?.[0],
      toProcess: alertDto?.toProcess,
      inProgress: alertDto?.inProgress,
      toAdjust: alertDto?.toAdjust,
      resolved: alertDto?.resolved,
      totalImpactFinancier: alertDto?.totalImpactFinancier ?? 0,
      total: alertDto?.total,
    };
  });

  return (
    <Box 
      height={"100%"} 
      width={"100%"} 
      bg={"white"} 
      px={2}
      py={4}
    >
      <Table
        loading={{ spinning: isLoading, tip: "Chargement en cours..."}}
        locale={isLoading ? { emptyText: null } : undefined}
        columns={columns}
        dataSource={storesMapped}
        pagination={false}
        sticky
        scroll={{ x: isBreakpoint === "phone" ? 900 : 1300,
            y: "calc(100vh - 280px)" }}
        summary={() => {
          return (
            <Table.Summary fixed="bottom">
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                  <Box>
                    <TextEllipsis bold size={"s"}>
                      Total
                    </TextEllipsis>
                  </Box>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <Box>
                    <TextEllipsis bold size={"s"}>
                      {synthesis?.totalToProcess}
                    </TextEllipsis>
                  </Box>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2}>
                  <Box>
                    <TextEllipsis bold size={"s"}>
                      {synthesis?.totalInProgress}
                    </TextEllipsis>
                  </Box>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3}>
                  <Box>
                    <TextEllipsis bold size={"s"}>
                      {synthesis?.totalToAdjust}
                    </TextEllipsis>
                  </Box>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4}>
                  <Box>
                    <TextEllipsis bold size={"s"}>
                      {synthesis?.totalResolved}
                    </TextEllipsis>
                  </Box>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5}>
                  <Box>
                    <TextEllipsis bold size={"s"}>
                      {synthesis?.total}
                    </TextEllipsis>
                  </Box>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6}>
                  <Box>
                    <TextEllipsis bold size={"s"}>
                      {!synthesis ? "" : (synthesis.totalImpactFinancier ?? 0) === 0 ? "Non chiffré" : formatCurrency(synthesis.totalImpactFinancier)}
                    </TextEllipsis>
                  </Box>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          );
        }}
      />
    </Box>
  );
};

export default RegionAlertTable;